import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type Item = {
  value: string;
  label: string;
  description?: string;
};

type Props = {
  items: Item[];
  value?: string;
  onChange: (value: string) => void;
  onInputChange?: (query: string) => void;
  placeholder?: string;
  loading?: boolean;
  className?: string;
  disabled?: boolean;
  visibleItems?: number;
};

export default function Autocomplete({
  items,
  value,
  onChange,
  onInputChange,
  placeholder,
  loading = false,
  className,
  disabled,
  visibleItems = 7,
}: Props) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const itemHeight = 40;
  const maxHeight = `${visibleItems * itemHeight}px`;
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const isKeyboardActivationRef = useRef(true);

  const selectedItem = useMemo(() => items.find((i) => i.value === value) ?? null, [items, value]);

  const filtered = useMemo(() => {
    if (!inputValue) return items;
    const q = inputValue.toLowerCase();
    return items.filter((i) => i.label.toLowerCase().includes(q));
  }, [items, inputValue]);

  // 閉じている間は選択済み項目の label を入力欄に表示する
  useEffect(() => {
    if (!open) setInputValue(selectedItem?.label ?? '');
  }, [open, selectedItem]);

  useEffect(() => {
    setActiveIndex((prev) => (prev < filtered.length ? prev : 0));
  }, [filtered.length]);

  useEffect(() => {
    // マウスホバー由来の activeIndex 変化ではスクロールしない（ホバー位置とスクロールの相互誘発を防ぐ）
    if (!open || !isKeyboardActivationRef.current) return;
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({
      block: 'nearest',
    });
  }, [open, activeIndex]);

  const selectItem = (item: Item) => {
    onChange(item.value);
    setInputValue(item.label);
    setOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      isKeyboardActivationRef.current = true;
      if (!open) {
        setActiveIndex(0);
        setOpen(true);
        return;
      }
      if (filtered.length === 0) return;
      setActiveIndex((prev) => {
        const next = event.key === 'ArrowDown' ? prev + 1 : prev - 1;
        return (next + filtered.length) % filtered.length;
      });
      return;
    }

    if (event.key === 'Enter' && open) {
      const item = filtered[activeIndex];
      if (item) {
        event.preventDefault();
        selectItem(item);
      }
    }
  };

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <div className={cn('relative w-full', className)}>
        <PopoverAnchor asChild>
          <div ref={anchorRef} className="relative">
            <MdSearch className="absolute top-1/2 left-3 z-10 size-icon-sm -translate-y-1/2 text-sub" />
            <input
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-autocomplete="list"
              autoComplete="off"
              className="h-xxl w-full rounded-sm border border-solid border-border bg-white px-9 text-body-sm focus:outline focus:outline-2 focus:outline-offset-[-2px] focus:outline-primary disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={placeholder}
              disabled={disabled}
              value={inputValue}
              onKeyDown={handleKeyDown}
              onChange={(e) => {
                const q = e.target.value;
                setInputValue(q);
                setActiveIndex(0);
                isKeyboardActivationRef.current = true;
                setOpen(true);
                onInputChange?.(q);
              }}
            />
          </div>
        </PopoverAnchor>
      </div>

      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
        }}
        className="w-(--radix-popover-trigger-width) overflow-x-hidden rounded-sm border border-border bg-white shadow-layer3!"
        style={{ maxHeight, overflowY: 'auto' }}
      >
        <div ref={listRef} role="listbox">
          {loading ? (
            <div className="px-3 py-2 text-body-sm text-sub">検索中...</div>
          ) : (
            filtered.map((i, index) => {
              const active = index === activeIndex;
              return (
                <div
                  key={i.value}
                  role="option"
                  aria-selected={i.value === value}
                  data-active={active}
                  className={cn(
                    'min-h-xxl cursor-pointer px-xs py-2 text-body-sm leading-relaxed select-none',
                    active ? 'bg-gray hover:bg-gray' : 'hover:bg-gray',
                    i.value === value && 'font-medium'
                  )}
                  onMouseEnter={() => {
                    isKeyboardActivationRef.current = false;
                    setActiveIndex(index);
                  }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectItem(i)}
                >
                  <div className="flex flex-col">
                    <span>{i.label}</span>
                    {i.description && <span className="text-xs text-sub">{i.description}</span>}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
